import React from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { CheckCircleIcon, ArrowRightIcon } from '@heroicons/react/24/solid';

const RentalYieldCalculatorLandingPage: React.FC = () => {
  const navigate = useNavigate();

  const features = [
    {
      title: 'Gross vs Net Yield',
      description: 'Calculate both gross and net rental yields accurately',
      icon: '📈',
    },
    {
      title: 'Expense Deductions',
      description: 'Factor in maintenance, taxes, insurance, and management costs',
      icon: '💰',
    },
    {
      title: 'Investment ROI',
      description: 'See your return on investment clearly and compare properties',
      icon: '🎯',
    },
    {
      title: 'Multiple Properties',
      description: 'Compare yields across different investment properties',
      icon: '🏢',
    },
    {
      title: 'Scenario Planning',
      description: 'Test different scenarios with varying rental income and expenses',
      icon: '📊',
    },
    {
      title: 'Irish Tax Rules',
      description: 'Accounts for Irish tax regulations and allowable deductions',
      icon: '⚖️',
    },
  ];

  const benefits = [
    'Evaluate investment potential accurately',
    'Compare multiple properties side-by-side',
    'Understand true profitability after expenses',
    'Make data-driven investment decisions',
    'Maximize your rental property returns',
  ];

  const faqItems = [
    {
      question: 'What is the difference between gross and net yield?',
      answer:
        'Gross yield uses rent versus property value before expenses; net yield subtracts expenses for a truer profitability picture.',
    },
    {
      question: 'Is this useful for lenders or partners?',
      answer:
        'Yes. Exportable breakdowns help you discuss returns with lenders, partners, or advisors.',
    },
  ];

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqItems.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  };

  return (
    <>
      <Helmet>
        <title>Rental Yield Calculator Ireland | Calculate Investment Returns | RentSmart.ie</title>
        <meta
          name="description"
          content="Calculate rental yield on your Irish investment property with RentSmart's free Rental Yield Calculator. See gross yield, net yield, and ROI in minutes."
        />
        <meta
          name="keywords"
          content="rental yield calculator, rental return calculator, investment property calculator, Ireland property, rental income calculator, ROI calculation"
        />
        <link
          rel="canonical"
          href="https://rentsmart.ie/property-tools/rental-yield-calculator-landing"
        />
        <meta property="og:title" content="Rental Yield Calculator - RentSmart.ie" />
        <meta
          property="og:description"
          content="Calculate rental yield and ROI on your investment property with RentSmart's free Rental Yield Calculator."
        />
        <meta property="og:type" content="website" />
        <meta
          property="og:url"
          content="https://rentsmart.ie/property-tools/rental-yield-calculator-landing"
        />
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50">
        {/* Hero Section */}
        <section className="pt-20 pb-16 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              Maximize Your Rental
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-blue-600">
                Investment Returns
              </span>
            </h1>
            <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
              Calculate accurate rental yields and compare investment properties. Make smarter
              investment decisions with instant ROI analysis.
            </p>
            <button
              onClick={() => navigate('/property-tools/rental-yield-calculator')}
              className="inline-flex items-center gap-2 bg-gradient-to-r from-indigo-600 to-indigo-700 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:shadow-lg transform hover:scale-105 transition-all duration-200"
            >
              Calculate Rental Yield
              <ArrowRightIcon className="w-5 h-5" />
            </button>
          </div>
        </section>

        {/* Features Grid */}
        <section className="py-16 px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-12 text-center">
              Professional Investment Analysis
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {features.map((feature, index) => (
                <div
                  key={index}
                  className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow border border-gray-100"
                >
                  <div className="text-4xl mb-4">{feature.icon}</div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-12 text-center">
              Smart Investment Planning
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {benefits.map((benefit, index) => (
                <div key={index} className="flex items-start gap-4">
                  <CheckCircleIcon className="w-6 h-6 text-indigo-500 flex-shrink-0 mt-1" />
                  <p className="text-lg text-gray-700">{benefit}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* What You'll Calculate */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-indigo-50">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-12 text-center">
              Key Metrics Calculated
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-white rounded-lg p-6 shadow-sm">
                <h3 className="text-2xl font-semibold text-indigo-600 mb-4">
                  📊 Gross Rental Yield
                </h3>
                <p className="text-gray-700">
                  Annual rental income divided by property value. Shows raw return before expenses.
                </p>
                <p className="text-gray-600 text-sm mt-3">
                  Typical range: 4-8% for Irish properties
                </p>
              </div>
              <div className="bg-white rounded-lg p-6 shadow-sm">
                <h3 className="text-2xl font-semibold text-indigo-600 mb-4">💰 Net Rental Yield</h3>
                <p className="text-gray-700">
                  Income minus all expenses, divided by property value. Shows actual profit.
                </p>
                <p className="text-gray-600 text-sm mt-3">More realistic measure of true returns</p>
              </div>
              <div className="bg-white rounded-lg p-6 shadow-sm">
                <h3 className="text-2xl font-semibold text-indigo-600 mb-4">
                  🎯 Return on Investment
                </h3>
                <p className="text-gray-700">
                  Your annual profit relative to your initial investment and capital.
                </p>
                <p className="text-gray-600 text-sm mt-3">Accounts for your down payment</p>
              </div>
              <div className="bg-white rounded-lg p-6 shadow-sm">
                <h3 className="text-2xl font-semibold text-indigo-600 mb-4">
                  📈 Cash Flow Analysis
                </h3>
                <p className="text-gray-700">
                  Monthly income after all expenses. Shows positive or negative cash flow.
                </p>
                <p className="text-gray-600 text-sm mt-3">
                  Essential for property portfolio planning
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Expenses Included */}
        <section className="py-16 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-12 text-center">
              All Expenses Factored In
            </h2>
            <div className="bg-white rounded-xl p-8 shadow-lg border border-gray-100">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">🏠 Property Costs</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li>✓ Maintenance and repairs</li>
                    <li>✓ Property management fees</li>
                    <li>✓ Letting agent commission</li>
                    <li>✓ Insurance premiums</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">💼 Financial Costs</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li>✓ Mortgage interest (not principal)</li>
                    <li>✓ Property taxes</li>
                    <li>✓ Ground rent (if applicable)</li>
                    <li>✓ Service charges</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    ⚖️ Legal & Compliance
                  </h3>
                  <ul className="space-y-2 text-gray-700">
                    <li>✓ Accounting and bookkeeping</li>
                    <li>✓ Legal fees</li>
                    <li>✓ Utilities (if landlord pays)</li>
                    <li>✓ Vacancy provisions</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">📋 Tax Allowances</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li>✓ Irish tax allowable deductions</li>
                    <li>✓ Capital allowances</li>
                    <li>✓ Depreciation calculations</li>
                    <li>✓ Relief on mortgage interest</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="py-16 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-12 text-center">
              Calculate in Minutes
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[
                {
                  step: 1,
                  title: 'Enter Property Details',
                  description: 'Property purchase price and current value',
                },
                {
                  step: 2,
                  title: 'Input Rental Income',
                  description: 'Annual or monthly rent and expected vacancy rate',
                },
                {
                  step: 3,
                  title: 'Add Expenses',
                  description: 'List all costs and get instant yield calculations',
                },
              ].map((item) => (
                <div key={item.step} className="text-center">
                  <div className="w-12 h-12 bg-indigo-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                    {item.step}
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">{item.title}</h3>
                  <p className="text-gray-600">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-indigo-600 to-blue-600">
          <div className="max-w-4xl mx-auto text-center text-white">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">
              Start Calculating Your Rental Yield Today
            </h2>
            <p className="text-lg mb-8 text-indigo-100">
              Make smarter investment decisions with accurate yield analysis
            </p>
            <button
              onClick={() => navigate('/property-tools/rental-yield-calculator')}
              className="inline-flex items-center gap-2 bg-white text-indigo-600 px-8 py-4 rounded-lg font-semibold text-lg hover:shadow-lg transform hover:scale-105 transition-all duration-200"
            >
              Use Yield Calculator
              <ArrowRightIcon className="w-5 h-5" />
            </button>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-10 text-center">
              Rental Yield Calculator FAQs
            </h2>
            <div className="space-y-6">
              {faqItems.map((item) => (
                <div
                  key={item.question}
                  className="bg-slate-50 border border-slate-200 rounded-lg p-6 shadow-sm"
                >
                  <div className="flex items-start gap-3">
                    <CheckCircleIcon className="w-6 h-6 text-indigo-600 mt-1" />
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900">{item.question}</h3>
                      <p className="text-gray-700 mt-2">{item.answer}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default RentalYieldCalculatorLandingPage;
